import { Asset, Preseed, TreeNode } from '@/types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

export interface ProvenanceNode {
  kind: 'seed' | 'preseed' | 'integration' | 'user';
  label: string;
  detail?: string;
  capability?: string;
  created?: string;
  visited?: string;
  key?: string;
}

function formatPreseedType(type: string): string {
  const map: Record<string, string> = {
    'whois+company': 'WHOIS Company',
    'whois+email': 'WHOIS Email',
    'whois+name': 'WHOIS Name',
    'edgar+company': 'EDGAR Company',
    csp: 'CSP Header',
    cik: 'SEC CIK',
    favicon: 'Favicon Hash',
    tlscert: 'TLS Certificate',
    analytics: 'Analytics ID',
    'cidr-handle': 'CIDR Handle',
  };
  return map[type] || normalizeOriginName(type);
}

function isIntegrationKey(key: string): boolean {
  return key.startsWith('#integration#');
}

function formatIntegrationLabel(asset: Asset): string {
  const id = asset.identifier || asset.group || '';
  if (id.startsWith('imports/seed/')) return 'Seed Import';
  if (id.startsWith('imports/')) return 'File Import';
  return normalizeOriginName(asset.group || id);
}

function extractPreseedFromNode(treeNode: TreeNode): Preseed | null {
  const result = treeNode.node?.result;
  if (!result?.preseeds?.length) return null;
  return result.preseeds[0];
}

function extractAssetFromNode(treeNode: TreeNode): Asset | null {
  const result = treeNode.node?.result as Record<string, unknown> | undefined;
  if (!result) return null;

  for (const key in result) {
    const value = result[key];
    if (!Array.isArray(value) || key === 'count') continue;

    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      const obj = item as Record<string, unknown>;
      if (obj.key && (obj.identifier || obj.name))
        return obj as unknown as Asset;
    }
  }
  return null;
}

function nodeToProvenance(treeNode: TreeNode): {
  prov: ProvenanceNode;
  key: string;
} | null {
  const discoveredData =
    treeNode.node?.relationships?.[0]?.result?.discovereds?.[0];

  const preseed = extractPreseedFromNode(treeNode);
  if (preseed) {
    return {
      key: preseed.key,
      prov: {
        kind: 'preseed',
        label: `${formatPreseedType(preseed.type)}: ${preseed.value || preseed.name}`,
        detail: preseed.title,
        capability: discoveredData?.capability,
        created: discoveredData?.created || preseed.created,
        visited: discoveredData?.visited,
        key: preseed.key,
      },
    };
  }

  const asset = extractAssetFromNode(treeNode);
  if (!asset) return null;

  if (isIntegrationKey(asset.key)) {
    return {
      key: asset.key,
      prov: {
        kind: 'integration',
        label: formatIntegrationLabel(asset),
        capability: discoveredData?.capability,
        created: discoveredData?.created || asset.created,
        visited: discoveredData?.visited,
        key: asset.key,
      },
    };
  }

  return {
    key: asset.key,
    prov: {
      kind: 'seed',
      label: asset.identifier || asset.group || asset.key,
      capability: discoveredData?.capability,
      created: discoveredData?.created || asset.created,
      visited: discoveredData?.visited || asset.visited,
      key: asset.key,
    },
  };
}

function findDirectParent(
  treeNodes: TreeNode[],
  targetKey: string
): TreeNode | null {
  const suffix = `#DISCOVERED${targetKey}`;
  for (const node of treeNodes) {
    const result = nodeToProvenance(node);
    if (!result || result.key === targetKey) continue;

    const rels = node.node?.relationships ?? [];
    for (const rel of rels) {
      for (const d of rel.result?.discovereds ?? []) {
        if (d.key?.endsWith(suffix)) return node;
      }
    }
  }
  return null;
}

function extractPiusProvider(seedCapabilities: string[]): string | undefined {
  const provider = seedCapabilities.find(
    c => c.startsWith('pius_') && c !== 'pius-discovery'
  );
  if (!provider) return undefined;
  return formatPiusProvider(provider.replace('pius_', ''));
}

function formatPiusProvider(slug: string): string {
  const map: Record<string, string> = {
    crtsh: 'crt.sh',
    'crt-sh': 'crt.sh',
    shodan: 'Shodan',
    censys: 'Censys',
    dnsdb: 'DNSDB',
    virustotal: 'VirusTotal',
    securitytrails: 'SecurityTrails',
    whoxy: 'Whoxy',
    'whoxy-reverse-whois': 'Whoxy',
    builtwith: 'BuiltWith',
    viewdns: 'ViewDNS',
    opencorporates: 'OpenCorporates',
    diffbot: 'Diffbot',
    apollo: 'Apollo',
    github: 'GitHub',
    'github-org': 'GitHub',
    'dns-brute': 'DNS Brute Force',
    'dns-zone-transfer': 'DNS Zone Transfer',
    'doh-enum': 'DoH Enumeration',
    'dns-permutation': 'DNS Permutation',
    'favicon-hash': 'Favicon Hash',
    'reverse-whois': 'Reverse WHOIS',
    'reverse-ip': 'Reverse IP',
    'passive-dns': 'Passive DNS',
    wayback: 'Wayback Machine',
    urlscan: 'urlscan.io',
    wikidata: 'Wikidata',
    'google-dorks': 'Google Dorks',
    'otx-alienvault': 'AlienVault OTX',
    'asn-bgp': 'ASN/BGP',
    'reverse-rir': 'Reverse RIR',
    edgar: 'EDGAR',
    arin: 'ARIN',
    ripe: 'RIPE',
    lacnic: 'LACNIC',
    apnic: 'APNIC',
    afrinic: 'AFRINIC',
    binaryedge: 'BinaryEdge',
    crunchbase: 'Crunchbase',
    proxycurl: 'Proxycurl',
    gleif: 'GLEIF',
  };
  return map[slug] || normalizeOriginName(slug);
}

export function processSeedProvenance(
  treeNodes: TreeNode[] | undefined,
  currentKey: string,
  seedCapabilities?: string[]
): ProvenanceNode[] {
  if (!treeNodes?.length) return [];

  const piusProvider = seedCapabilities
    ? extractPiusProvider(seedCapabilities)
    : undefined;

  const chain: ProvenanceNode[] = [];
  const visited = new Set<string>();
  let targetKey = currentKey;

  for (let step = 0; step < 5; step++) {
    const parent = findDirectParent(treeNodes, targetKey);
    if (!parent) break;

    const result = nodeToProvenance(parent);
    if (!result || result.key === currentKey) break;
    if (visited.has(result.key)) break;

    visited.add(result.key);

    if (
      piusProvider &&
      result.prov.capability === 'pius-discovery' &&
      chain.length === 0
    ) {
      result.prov.capability = `pius-discovery:${piusProvider}`;
    }

    chain.push(result.prov);
    targetKey = result.key;
  }

  return chain;
}

function preseedDescription(type: string): string {
  const map: Record<string, string> = {
    'whois+company': 'WHOIS company name',
    'whois+email': 'WHOIS registrant email',
    'whois+name': 'WHOIS registrant name',
    'edgar+company': 'SEC filing company name',
    csp: 'CSP header match',
    cik: 'SEC CIK number',
    favicon: 'favicon hash',
    tlscert: 'TLS certificate match',
    analytics: 'analytics tracking ID',
    'cidr-handle': 'CIDR handle',
  };
  return map[type] || 'organizational signal';
}

function preseedTypeFromKey(key: string | undefined): string {
  if (!key) return '';
  return key.replace('#preseed#', '').split('#')[0];
}

function preseedValueFromLabel(label: string): string {
  return label.includes(':') ? label.split(': ').slice(1).join(': ') : label;
}

function parseCapability(raw: string | undefined): {
  name: string;
  provider?: string;
} {
  if (!raw) return { name: '' };
  if (raw.startsWith('pius-discovery:')) {
    return { name: 'PIUS Discovery', provider: raw.split(':')[1] };
  }
  return { name: normalizeOriginName(raw) };
}

export function formatProvenanceSummary(
  node: ProvenanceNode,
  prev: ProvenanceNode | undefined
): string {
  const { name: cap, provider } = parseCapability(node.capability);

  const via = provider ? `${cap} (${provider})` : cap || undefined;

  switch (node.kind) {
    case 'preseed': {
      const desc = preseedDescription(preseedTypeFromKey(node.key));
      const value = preseedValueFromLabel(node.label);
      return via
        ? `${via} matched ${desc} "${value}"`
        : `Matched ${desc} "${value}"`;
    }
    case 'seed': {
      if (prev?.kind === 'preseed') {
        const desc = preseedDescription(preseedTypeFromKey(prev.key));
        const value = preseedValueFromLabel(prev.label);
        return `${via ?? 'Scanning'} on ${node.label} found ${desc} "${value}"`;
      }
      return via ? `${via} ran on ${node.label}` : `From ${node.label}`;
    }
    case 'integration':
      if (prev) {
        return `${prev.label} was added via ${node.label}`;
      }
      return `Added via ${node.label}`;
    case 'user':
      return `Added by ${node.label}`;
  }
}
